/**
 * Capture a partner (contractor / installer) by hand, mid-workflow (WPS-163).
 *
 * Not every partner is a member of the tenant's partner network, so an agent
 * filing a registration or a claim needs to create one without leaving the
 * form they are in. `useCreateManualPartner` creates the partner and returns
 * the partnership, ready to assign. Duplicate detection is not a separate live
 * call: the search the agent came through already surfaces existing partners,
 * and on save the backend re-runs the check and answers 409 with candidates
 * unless `acknowledge_duplicates` is set, so a near-duplicate is still caught.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'
import type { Partnership } from '@/admin/types/organisations'

export interface DuplicatePartnerCandidate {
  partnership_uuid: string
  organisation_uuid: string
  name: string
  city: string
  state: string
  similarity: number
  is_active: boolean
}

export interface ManualPartnerContactInput {
  name?: string
  email?: string
  phone?: string
}

export interface ManualPartnerInput {
  company_name: string
  city: string
  state: string
  role_uuid?: string | null
  address_line_1?: string
  address_line_2?: string
  postcode?: string
  business_phone?: string
  company_email?: string
  contacts?: ManualPartnerContactInput[]
  acknowledge_duplicates?: boolean
}

export function useCreateManualPartner() {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation<Partnership, unknown, ManualPartnerInput>({
    mutationFn: (payload) =>
      api
        .post(`/organisations/${orgUuid}/partnerships/manual/`, payload)
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => {
      // The new partner has to show up in the next search straight away -
      // the agent who just created it is about to attach it to something.
      queryClient.invalidateQueries({ queryKey: ['partnerships'] })
      queryClient.invalidateQueries({ queryKey: ['partnerships-search'] })
    },
  })
}

export interface ManualPartnerUpdateInput {
  partnershipUuid: string
  company_name?: string
  city?: string
  state?: string
  address_line_1?: string
  address_line_2?: string
  postcode?: string
  business_phone?: string
  company_email?: string
  contacts?: ManualPartnerContactInput[]
}

/**
 * Correct a hand-captured partner in place, for "Edit Contractor" on the
 * assigned-contractor section. The API refuses a partner the partner network
 * supplied, since the next sync would overwrite the edit.
 */
export function useUpdateManualPartner() {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation<Partnership, unknown, ManualPartnerUpdateInput>({
    mutationFn: ({ partnershipUuid, ...payload }) =>
      api
        .put(
          `/organisations/${orgUuid}/partnerships/${partnershipUuid}/manual/`,
          payload,
        )
        .then((r) => r.data?.data ?? r.data),
    onSuccess: (_data, variables) => {
      // The profile panel reads the detail endpoint, so it has to refetch for
      // the correction to appear where the agent made it.
      queryClient.invalidateQueries({
        queryKey: ['partnership-detail', orgUuid, variables.partnershipUuid],
      })
      queryClient.invalidateQueries({ queryKey: ['partnerships'] })
      queryClient.invalidateQueries({ queryKey: ['partnerships-search'] })
      // A claim embeds the resolved contractor in its own payload, so editing
      // one from a claim has to refetch the claim too - otherwise the card the
      // agent just corrected keeps showing the old details.
      queryClient.invalidateQueries({ queryKey: ['claim'] })
    },
  })
}

/**
 * Set, change or clear the partner a claim concerns.
 *
 * Passing `null` clears the override, which returns the claim to inheriting
 * its registration's partner rather than leaving it with none - so the
 * response reports which of the two the claim ended up on.
 */
export function useSetClaimPartner(claimUuid: string) {
  const queryClient = useQueryClient()
  return useMutation<
    { partner: Partnership | null; partner_is_inherited: boolean },
    unknown,
    { partnershipUuid: string | null }
  >({
    mutationFn: ({ partnershipUuid }) =>
      api
        .put(`/claims/claims/${claimUuid}/partner/`, {
          partnership_uuid: partnershipUuid,
        })
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim', claimUuid] })
    },
  })
}
